import { useCallback, useMemo, useState, useSyncExternalStore } from "react";
import { useLocation } from "wouter";
import { useMediaQuery } from "usehooks-ts";
import type { EventId, PlanEvent, Priority, Task, TaskId } from "../../../shared/types";
import type { DesktopCalendarViewProps } from "../types/desktop-calendar-view";
import type { DayHeaderAction } from "../types/desktop-calendar-view";
import { ConfirmDialog } from "../../../ui/atoms/ConfirmDialog";
import { ContextMenu } from "../../../ui/atoms/ContextMenu";
import { MonthGrid } from "./MonthGrid";
import { WeekGrid } from "./week-grid/WeekGrid";
import { MiniCalendar } from "./MiniCalendar";
import { CalendarNavigation } from "./CalendarNavigation";
import { CalendarSidebar } from "./CalendarSidebar";
import { CalendarDialogs } from "./dialogs/CalendarDialogs";
import { EventQuickViewModal } from "./dialogs/EventQuickViewModal";
import { TaskQuickViewModal } from "./dialogs/TaskQuickViewModal";
import { AddTaskInlineDialog } from "./dialogs/AddTaskInlineDialog";
import { cycleStatus, addTask } from "../../tasks/store/task-store";
import { setClipboardEvent, useClipboardEvent } from "../stores/clipboard-store";
import { RecurrenceScopeDialog } from "./dialogs/RecurrenceScopeDialog";
import { useHolidays } from "../../../shared/hooks/use-holidays";
import { showHolidaysStore } from "../../../hooks/useAppState";

export type { DesktopCalendarViewProps };

const FULLSCREEN_PATH = "/calendar/fullscreen";

export function DesktopCalendarView({
  nav,
  today,
  displayDays,
  now,
  events,
  tasks,
  eventsByDate,
  tasksByDate,
  selectedEvents,
  editingEvent,
  showAddForm,
  showFullEditForm,
  selectedDate,
  formStartTime,
  formEndTime,
  formAnchor,
  activeCategory,
  pendingRecurrence,
  pendingResync,
  recurrenceToast,
  googleCalendarUi = false,
  readOnly,
  onToggleReadOnly,
  onHandleEventClick,
  onHandleEventLongPress,
  onHandleSlotClick,
  onHandleUpdateEvent,
  onHandleOpenAdd,
  onSetEditingEvent,
  onAddEvent,
  onAddEventFull,
  onDeleteEvent,
  onUpdateEvent,
  onSetShowFullEditForm,
  onSetPendingResync,
  onSetRecurrenceToast,
  onHandleDuplicate,
  onAddRecurrenceException,
  onUndoEvent,
  onCloseAddForm,
  onHandleRecurrenceChoice,
  onHandleResyncChoice,
  onSetPendingRecurrence,
}: DesktopCalendarViewProps) {
  const [location, navigate] = useLocation();
  const isFullscreen = location === FULLSCREEN_PATH;
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const [addTaskDay, setAddTaskDay] = useState<string | null>(null);
  const [deleteDayEventsDay, setDeleteDayEventsDay] = useState<string | null>(null);
  const [quickViewTaskId, setQuickViewTaskId] = useState<TaskId | null>(null);
  const [ctxMenu, setCtxMenu] = useState<{ event: PlanEvent; anchorRect: DOMRect } | null>(null);
  const [dayCtxMenu, setDayCtxMenu] = useState<{ date: string; time: string; anchorRect: DOMRect } | null>(null);
  const clipboardEvent = useClipboardEvent();
  const holidaysEnabled = useSyncExternalStore(showHolidaysStore.subscribe, showHolidaysStore.get);
  const allHolidays = useHolidays(displayDays);
  const holidays = holidaysEnabled ? allHolidays : undefined;

  const handleEventContextMenu = useCallback(
    (event: PlanEvent, anchorRect: DOMRect) => {
      setDayCtxMenu(null);
      setCtxMenu({ event, anchorRect });
    },
    [],
  );

  const handleDayContextMenu = useCallback(
    (date: string, time: string, anchorRect: DOMRect) => {
      setCtxMenu(null);
      setDayCtxMenu({ date, time, anchorRect });
    },
    [],
  );

  const handleTaskClick = useCallback((task: Task) => {
    setQuickViewTaskId(task.id);
  }, []);

  const deleteDayEvents = useMemo(
    () => eventsByDate.get(deleteDayEventsDay ?? "") ?? [],
    [eventsByDate, deleteDayEventsDay],
  );

  const handleDayAction = useCallback(
    (day: string, action: DayHeaderAction) => {
      switch (action) {
        case "addEvent":
          onHandleSlotClick(day, "09:00", "10:00");
          break;
        case "addTask":
          setAddTaskDay(day);
          break;
        case "navigateToDay":
          nav.goToDate(day);
          nav.setCalendarMode("day");
          break;
        case "deleteDayEvents":
          setDeleteDayEventsDay(day);
          break;
      }
    },
    [onHandleSlotClick, nav],
  );

  const handleAddTaskConfirm = useCallback(
    (title: string, category: string, priority: Priority) => {
      if (addTaskDay) addTask(title, category, addTaskDay, priority);
      setAddTaskDay(null);
    },
    [addTaskDay],
  );

  const handleDeleteDayEventsConfirm = useCallback(() => {
    for (const event of deleteDayEvents) {
      onDeleteEvent(event.id);
    }
    setDeleteDayEventsDay(null);
  }, [deleteDayEvents, onDeleteEvent]);

  const handleCopyEvent = useCallback((event: PlanEvent) => {
    setClipboardEvent(event);
  }, []);

  const handlePasteEvent = useCallback(
    (targetDate: string, startTime?: string) => {
      if (!clipboardEvent || !onAddEventFull) return;
      const resolvedStart = startTime ?? clipboardEvent.startTime;
      const [sh, sm] = clipboardEvent.startTime.split(":").map(Number);
      const [eh, em] = clipboardEvent.endTime.split(":").map(Number);
      const durationMin = ((eh ?? 0) * 60 + (em ?? 0)) - ((sh ?? 0) * 60 + (sm ?? 0));
      const [rh, rm] = resolvedStart.split(":").map(Number);
      const endMin = Math.min((rh ?? 0) * 60 + (rm ?? 0) + durationMin, 24 * 60);
      const resolvedEnd = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
      onAddEventFull({
        title: `${clipboardEvent.title} (copie)`,
        category: clipboardEvent.category,
        date: targetDate,
        startTime: resolvedStart,
        endTime: resolvedEnd,
        description: clipboardEvent.description,
        notes: clipboardEvent.notes,
        tags: [...clipboardEvent.tags],
        recurrence: clipboardEvent.recurrence,
        reminderMinutes: clipboardEvent.reminderMinutes,
        reminderTime: clipboardEvent.reminderTime ?? null,
        reminderType: clipboardEvent.reminderType ?? null,
      });
    },
    [clipboardEvent, onAddEventFull],
  );

  const [recurrenceDeleteEvent, setRecurrenceDeleteEvent] = useState<PlanEvent | null>(null);

  const buildCtxMenuItems = useCallback(
    (event: PlanEvent) => {
      const items: { label?: string; onClick?: () => void; danger?: boolean; separator?: boolean }[] = [
        {
          label: "Copier",
          onClick: () => handleCopyEvent(event),
        },
        ...(clipboardEvent
          ? [
              {
                label: "Coller",
                onClick: () => handlePasteEvent(event.date),
              },
            ]
          : []),
        { separator: true },
        {
          label: "Dupliquer",
          onClick: () => onHandleDuplicate(event),
        },
        {
          label: "Modifier",
          onClick: () => {
            onSetEditingEvent(event);
            onSetShowFullEditForm(true);
          },
        },
        {
          label: "Supprimer",
          danger: true,
          onClick: () => {
            if (event.recurrence?.type && onAddRecurrenceException) {
              setRecurrenceDeleteEvent(event);
            } else {
              onDeleteEvent(event.id);
            }
          },
        },
      ];
      return items;
    },
    [clipboardEvent, handleCopyEvent, handlePasteEvent, onHandleDuplicate, onDeleteEvent, onAddRecurrenceException, onSetEditingEvent, onSetShowFullEditForm],
  );

  const dialogs = (
    <CalendarDialogs
      showAddForm={showAddForm}
      showFullEditForm={showFullEditForm}
      selectedDate={selectedDate}
      formStartTime={formStartTime}
      formEndTime={formEndTime}
      formAnchor={formAnchor}
      editingEvent={editingEvent}
      selectedEvents={selectedEvents}
      activeCategory={activeCategory}
      pendingRecurrence={pendingRecurrence}
      pendingResync={pendingResync}
      recurrenceToast={recurrenceToast}
      onAddEvent={onAddEvent}
      onAddEventFull={onAddEventFull}
      onUpdateEvent={onUpdateEvent}
      onCloseAddForm={onCloseAddForm}
      onSetShowFullEditForm={onSetShowFullEditForm}
      onSetEditingEvent={onSetEditingEvent}
      onHandleRecurrenceChoice={onHandleRecurrenceChoice}
      onHandleResyncChoice={onHandleResyncChoice}
      onSetPendingRecurrence={onSetPendingRecurrence}
      onSetPendingResync={onSetPendingResync}
      onSetRecurrenceToast={onSetRecurrenceToast}
    />
  );

  const quickViewModal = editingEvent && !showFullEditForm && (
    <EventQuickViewModal
      event={editingEvent}
      events={events}
      formAnchor={formAnchor}
      readOnly={readOnly}
      onClose={() => onSetEditingEvent(null)}
      onDeleteEvent={onDeleteEvent}
      onSetEditingEvent={onSetEditingEvent}
      onOpenFullEdit={() => onSetShowFullEditForm(true)}
      onSetPendingResync={onSetPendingResync}
      onSetRecurrenceToast={onSetRecurrenceToast}
      onDuplicate={onHandleDuplicate}
      onAddRecurrenceException={onAddRecurrenceException}
      onUndoEvent={onUndoEvent}
      onAddEventFull={onAddEventFull}
    />
  );

  const dayActionDialogs = (
    <>
      <AddTaskInlineDialog
        open={addTaskDay !== null}
        defaultCategory={activeCategory}
        onConfirm={handleAddTaskConfirm}
        onCancel={() => setAddTaskDay(null)}
      />
      <ConfirmDialog
        open={deleteDayEventsDay !== null && deleteDayEvents.length > 0}
        title="Supprimer les événements"
        message={`Supprimer ${deleteDayEvents.length} événement${deleteDayEvents.length > 1 ? "s" : ""} du jour ?`}
        confirmLabel="Supprimer"
        variant="danger"
        onConfirm={handleDeleteDayEventsConfirm}
        onCancel={() => setDeleteDayEventsDay(null)}
      />
    </>
  );

  const contextMenus = (
    <>
      {ctxMenu && (
        <ContextMenu
          anchorRect={ctxMenu.anchorRect}
          onClose={() => setCtxMenu(null)}
          items={buildCtxMenuItems(ctxMenu.event)}
        />
      )}
      {dayCtxMenu && !ctxMenu && clipboardEvent && (
        <ContextMenu
          anchorRect={dayCtxMenu.anchorRect}
          onClose={() => setDayCtxMenu(null)}
          items={[
            {
              label: "Coller",
              onClick: () => handlePasteEvent(dayCtxMenu.date, dayCtxMenu.time),
            },
          ]}
        />
      )}
      <RecurrenceScopeDialog
        open={!!recurrenceDeleteEvent}
        mode="delete"
        onThis={() => {
          if (!recurrenceDeleteEvent) return;
          const suffix = `_${recurrenceDeleteEvent.date}`;
          const baseId = (recurrenceDeleteEvent.id.endsWith(suffix)
            ? recurrenceDeleteEvent.id.slice(0, -suffix.length)
            : recurrenceDeleteEvent.id) as EventId;
          onAddRecurrenceException?.(baseId, recurrenceDeleteEvent.date);
          setRecurrenceDeleteEvent(null);
        }}
        onAll={() => {
          if (!recurrenceDeleteEvent) return;
          const suffix = `_${recurrenceDeleteEvent.date}`;
          const baseId = (recurrenceDeleteEvent.id.endsWith(suffix)
            ? recurrenceDeleteEvent.id.slice(0, -suffix.length)
            : recurrenceDeleteEvent.id) as EventId;
          onDeleteEvent(baseId, undefined, true);
          setRecurrenceDeleteEvent(null);
        }}
        onCancel={() => setRecurrenceDeleteEvent(null)}
      />
    </>
  );

  if (isFullscreen) {
    return (
      <div className={`h-full flex flex-col px-4 py-3 ${nav.calendarMode === "day" ? "max-w-md mx-auto w-full" : ""}`}>
        <WeekGrid
          days={displayDays}
          today={today}
          selectedDate={selectedDate}
          holidays={holidays}
          now={now}
          eventsByDate={eventsByDate}
          tasksByDate={tasksByDate}
          isExpanded
          readOnly={readOnly}
          showAddForm={showAddForm}
          onEventClick={onHandleEventClick}
          onEventLongPress={onHandleEventLongPress}
          onEventContextMenu={handleEventContextMenu}
          onSlotClick={onHandleSlotClick}
          onUpdateEvent={onHandleUpdateEvent}
          onCycleTaskStatus={cycleStatus}
          onTaskClick={handleTaskClick}
          onToggleExpand={() => navigate("/calendar")}
          onDayAction={handleDayAction}
          onDayContextMenu={handleDayContextMenu}
        />
        {isDesktop && (
          <>
            {quickViewModal}
            {!readOnly && quickViewTaskId && (
              <TaskQuickViewModal
                taskId={quickViewTaskId}
                onClose={() => setQuickViewTaskId(null)}
                readOnly={readOnly}
              />
            )}
            {!readOnly && dialogs}
            {!readOnly && dayActionDialogs}
          </>
        )}
        {!readOnly && contextMenus}
      </div>
    );
  }

  // Layout par défaut : contenu à gauche, sidebar à droite
  if (!googleCalendarUi) {
    return (
      <div className="hidden md:flex flex-col xl:flex-row gap-4 xl:gap-6 xl:items-start relative h-full">
        {/* Navigation compacte visible uniquement sur tablette (sidebar cachée) */}
        <div className="xl:hidden shrink-0 max-w-3xl mx-auto w-full">
          <CalendarNavigation
            currentDate={nav.currentDate}
            selectedDate={nav.currentDate}
            calendarMode={nav.calendarMode}
            onPrev={nav.goToPrev}
            onNext={nav.goToNext}
            onToday={nav.goToToday}
            onGoToDate={nav.goToDate}
            onModeChange={nav.setCalendarMode}
            readOnly={readOnly}
            onToggleReadOnly={onToggleReadOnly}
          />
        </div>
        <div className={`flex-1 min-w-0 min-h-0 h-full flex ${nav.calendarMode === "day" ? "max-w-3xl mx-auto w-full" : ""}`}>
          <div className="flex-1 min-w-0 h-full flex flex-col">
            {nav.calendarMode === "month" ? (
              <MonthGrid
                days={displayDays}
                currentDate={nav.currentDate}
                today={today}
                selectedDate={selectedDate ?? null}
                eventsByDate={eventsByDate}
                tasksByDate={tasksByDate}
                holidays={holidays}
                onDayClick={(day: string) => {
                  nav.goToDate(day);
                  nav.setCalendarMode("week");
                }}
              />
            ) : (
              <WeekGrid
                days={displayDays}
                today={today}
                selectedDate={selectedDate}
                holidays={holidays}
                now={now}
                eventsByDate={eventsByDate}
                tasksByDate={tasksByDate}
                showAddForm={showAddForm}
                onEventClick={onHandleEventClick}
                onEventLongPress={onHandleEventLongPress}
                onEventContextMenu={handleEventContextMenu}
                onSlotClick={onHandleSlotClick}
                onUpdateEvent={onHandleUpdateEvent}
                onCycleTaskStatus={cycleStatus}
                onTaskClick={handleTaskClick}
                onToggleExpand={() => navigate(FULLSCREEN_PATH)}
                onDayAction={handleDayAction}
                onDayContextMenu={handleDayContextMenu}
                readOnly={readOnly}
                onToggleReadOnly={onToggleReadOnly}
              />
            )}
          </div>
          {nav.calendarMode === "day" && (
            <aside className="w-60 shrink-0 border-l border-border/15 px-3 py-4 overflow-y-auto scrollbar-custom">
              <DayPanelTasks
                date={nav.currentDate}
                tasks={tasks}
                onTaskClick={handleTaskClick}
                onCycleStatus={cycleStatus}
              />
            </aside>
          )}
        </div>

        <div className="hidden xl:block">
          <CalendarSidebar
            nav={nav}
            events={events}
            tasks={tasks}
            onHandleOpenAdd={onHandleOpenAdd}
            variant="default"
            holidays={holidays}
          />
        </div>

        {isDesktop && (
          <>
            {quickViewModal}
            {!readOnly && quickViewTaskId && (
              <TaskQuickViewModal
                taskId={quickViewTaskId}
                onClose={() => setQuickViewTaskId(null)}
                readOnly={readOnly}
              />
            )}
            {!readOnly && dialogs}
            {!readOnly && dayActionDialogs}
          </>
        )}
        {!readOnly && contextMenus}
      </div>
    );
  }

  // Layout Google Calendar : header en haut, sidebar gauche + contenu droite
  return (
    <div className="hidden md:flex flex-col h-full">
      {/* Header bar avec navigation Google Calendar style */}
      <CalendarNavigation
        currentDate={nav.currentDate}
        selectedDate={nav.currentDate}
        calendarMode={nav.calendarMode}
        onPrev={nav.goToPrev}
        onNext={nav.goToNext}
        onToday={nav.goToToday}
        onGoToDate={nav.goToDate}
        onModeChange={nav.setCalendarMode}
        variant="google"
        readOnly={readOnly}
        onToggleReadOnly={onToggleReadOnly}
      />

      {/* Contenu principal : sidebar gauche + grille droite */}
      <div className="flex flex-1 min-h-0 py-4">
        {/* Sidebar gauche */}
        <aside className="w-60 shrink-0 px-3 flex flex-col gap-3">
          {/* Bouton Créer */}
          {onHandleOpenAdd && (
            <button
              type="button"
              onClick={onHandleOpenAdd}
              aria-label="Nouvel événement"
              className="flex items-center justify-center gap-3 px-5 py-2 w-full
                rounded-2xl bg-surface shadow-md hover:shadow-lg
                border border-border/20
                active:scale-[0.98] transition-all duration-150
                focus:outline-none focus:ring-2 focus:ring-accent/40 group"
            >
              <svg className="w-7 h-7 text-accent" viewBox="0 0 36 36" fill="none">
                <path d="M18 8v20M8 18h20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
              <span className="text-sm font-medium text-ink pr-1">Créer</span>
            </button>
          )}
          <MiniCalendar
            events={events}
            tasks={tasks}
            referenceDate={nav.currentDate}
            onSelectDate={nav.goToDate}
            variant="google"
          />
          <DayPanelTasks
            date={nav.selectedDate ?? nav.currentDate}
            tasks={tasks}
            onTaskClick={handleTaskClick}
            onCycleStatus={cycleStatus}
          />
        </aside>

        {/* Contenu principal */}
        <div className={`flex-1 min-w-0 h-full flex flex-col ${nav.calendarMode === "day" ? "max-w-2xl mx-auto w-full overflow-hidden" : ""}`}>
            {nav.calendarMode === "month" ? (
              <MonthGrid
                days={displayDays}
                currentDate={nav.currentDate}
                today={today}
                selectedDate={selectedDate ?? null}
                eventsByDate={eventsByDate}
                tasksByDate={tasksByDate}
                holidays={holidays}
                onDayClick={(day: string) => {
                  nav.goToDate(day);
                  nav.setCalendarMode("week");
                }}
              />
            ) : (
              <WeekGrid
                days={displayDays}
                today={today}
                selectedDate={selectedDate}
                holidays={holidays}
                now={now}
                eventsByDate={eventsByDate}
                tasksByDate={tasksByDate}
                showAddForm={showAddForm}
                onEventClick={onHandleEventClick}
                onEventLongPress={onHandleEventLongPress}
                onEventContextMenu={handleEventContextMenu}
                onSlotClick={onHandleSlotClick}
                onUpdateEvent={onHandleUpdateEvent}
                onCycleTaskStatus={cycleStatus}
                onTaskClick={handleTaskClick}
                onToggleExpand={() => navigate(FULLSCREEN_PATH)}
                onDayAction={handleDayAction}
                onDayContextMenu={handleDayContextMenu}
                readOnly={readOnly}
                onToggleReadOnly={onToggleReadOnly}
              />
            )}
          </div>
      </div>

      {isDesktop && !readOnly && (
        <>
          {quickViewModal}
          {quickViewTaskId && (
            <TaskQuickViewModal
              taskId={quickViewTaskId}
              onClose={() => setQuickViewTaskId(null)}
              readOnly={readOnly}
            />
          )}
          {dialogs}
          {dayActionDialogs}
        </>
      )}
      {!readOnly && contextMenus}
    </div>
  );
}

import { CATEGORY_DOT_COLORS, STATUS_LABELS, PRIORITY_COLORS } from "../../../shared/types";
import { getDayName, getDayNumber } from "../../../shared/types/date-helpers";

function DayPanelTasks({
  date,
  tasks,
  onTaskClick,
  onCycleStatus,
}: {
  readonly date: string;
  readonly tasks: Task[];
  readonly onTaskClick: (task: Task) => void;
  readonly onCycleStatus: (id: TaskId) => void;
}) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const dayTasks = useMemo(
    () => tasks.filter((t) => t.dueDate === date && !t.eventId && !t.archived),
    [tasks, date],
  );
  const doneTasks = dayTasks.filter((t) => t.status === "done").length;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-ink">
          Tâches — {getDayName(date)} {getDayNumber(date)}
        </h3>
        <div className="flex items-center gap-2">
          {dayTasks.length > 0 && (
            <span className="text-[10px] text-ink-faint tabular-nums">{doneTasks}/{dayTasks.length}</span>
          )}
          <button
            type="button"
            onClick={() => setShowAddDialog(true)}
            className="text-[10px] font-semibold text-accent hover:text-accent/70 transition-colors"
          >
            + Tâche
          </button>
        </div>
      </div>
      {dayTasks.length > 0 && (
        <div className="flex flex-col gap-1">
          {dayTasks.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onTaskClick(t)}
              className="w-full flex items-center gap-2 rounded-lg px-2.5 py-2 hover:bg-surface-alt transition-colors text-left group"
            >
              <span className={`w-2 h-2 rounded-full shrink-0 ${CATEGORY_DOT_COLORS[t.category]}`} />
              <span className="text-xs text-ink truncate flex-1">{t.title}</span>
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  onCycleStatus(t.id);
                }}
                className={`text-[10px] shrink-0 hover:text-accent cursor-pointer transition-colors ${PRIORITY_COLORS[t.priority]}`}
              >
                {STATUS_LABELS[t.status]}
              </span>
            </button>
          ))}
        </div>
      )}
      {dayTasks.length === 0 && (
        <p className="text-xs text-ink-faint">Aucune tâche pour ce jour</p>
      )}
      <AddTaskInlineDialog
        open={showAddDialog}
        defaultCategory="personal"
        onConfirm={(title, category, priority) => {
          addTask(title, category, date, priority);
          setShowAddDialog(false);
        }}
        onCancel={() => setShowAddDialog(false)}
      />
    </div>
  );
}
